import {EstimateTreeDto} from '../../../shared/dtos/estimate-tree.dto';
import {EstimateTreeEquipmentDto} from '../../../shared/dtos/estimate-tree-equipment.dto';
import {lockedCost} from '../../../shared/pricing/equipment-charges';
import {WorkOrderStatus, WorkOrderStatusType} from '../../work-order/dtos/work-order.dto';
import {equipmentName, equipmentRow, formatMinutes, laborMinutes, money} from './record-math';

/*
 * The job's rules and numbers (SQ-597), as Android works them out:
 * JobStatusActions.kt (the bar), CompleteJob.kt (what a status allows),
 * JobSchedule.kt (the schedule's wording), JobTimerView.kt (the crew clocks),
 * JobReview.kt (finishing: the checks, the time moved between work items,
 * equipment's actual time) and JobTimeAllocation.kt (Was It Bid Right?).
 * Every figure here is the one Android shows for the same record.
 */

// ---- Status ------------------------------------------------------------------------------------

const COMPLETABLE: string[] = [WorkOrderStatus.Confirmed, WorkOrderStatus.Scheduled, WorkOrderStatus.InProgress];
const COMPLETED: string[] = [WorkOrderStatus.Completed, WorkOrderStatus.Invoiced, WorkOrderStatus.PendingReInvoice];
const INVOICED: string[] = [WorkOrderStatus.Invoiced, WorkOrderStatus.PendingReInvoice];

/** Confirmed, Scheduled or In Progress: Finish the Job is offered. */
export const isOpenJob = (status?: string | null) => !!status && COMPLETABLE.includes(status);

/** Completed, and the invoicing tail: the work is done and the page shows how the bid held up. */
export const isCompletedJob = (status?: string | null) => !!status && COMPLETED.includes(status);

export const isCancelledJob = (status?: string | null) => status === WorkOrderStatus.Cancelled;
export const isOnHoldJob = (status?: string | null) => status === WorkOrderStatus.OnHold;

/** canEditWorkOrder: Edit on Confirmed, Scheduled, In Progress and (corrected in place) Completed. */
export const isEditableJob = (status?: string | null) => isOpenJob(status) || status === WorkOrderStatus.Completed;

export const jobHasSchedule = (date?: string | null, time?: string | null) => !!date?.trim() || !!time?.trim();

// ---- What the person is sent (SQ-636) --------------------------------------------------------------

/**
 * The person is sent what a job's time split and Was It Bid Right? are made
 * from, as the record's read says (JobStore.seesAllocation on iPhone,
 * showsTimeSplit / showsBidSummary on Android): its estimates and prices
 * (not pricesMasked) and its operations' actual times (not actualsMasked; a
 * Crew Leader is sent the prices but not the actuals). Without them a saved
 * review reads as none and the split as even, so the page shows no tan cards,
 * no allocated or actual time, no verdict and no equipment over/under, and
 * sends no split: it would replace a review they were never sent (the server
 * refuses it once the job is finished). Only a read that says both are shown
 * counts: an API from before SQ-636 says neither, and may have hidden them.
 */
export const seesActuals = (estimate?: {pricesMasked?: boolean; actualsMasked?: boolean}) =>
  estimate?.pricesMasked === false && estimate.actualsMasked === false;

/** The server's reason for refusing that split, which the page gives on the tap. */
export const ALLOCATION_HIDDEN = "Your role can't see this job's recorded time split, so it can't replace it.";

// ---- The bar (JobStatusActions.kt) ---------------------------------------------------------------

export type JobAction = 'schedule' | 'hold' | 'cancel';

export type JobActionOutcome =
  | {kind: 'open-schedule'}
  | {kind: 'confirm-reopen'}
  | {kind: 'apply'; status: WorkOrderStatusType}
  | {kind: 'confirm'; action: JobAction; status: WorkOrderStatusType; message: string}
  | {kind: 'blocked'; message: string};

/** Asked from both Reopen buttons before a completion is undone. */
export const REOPEN_JOB_CONFIRM_MESSAGE = 'Reopen this job? It goes back to Confirmed so it can be scheduled and worked again. '
  + "Nothing recorded on it is lost, but the Was It Bid Right results come off until it's finished again.";

/** The Schedule slot's label: Reopen Job on a completed job. */
export const scheduleSlotLabel = (status?: string | null) => status === WorkOrderStatus.Completed ? 'Reopen Job' : 'Schedule';

const blocked = (action: JobAction, reason: 'cancelled' | 'completed' | 'invoiced'): JobActionOutcome => {
  const hold = action === 'hold';
  switch (reason) {
    case 'cancelled': return {kind: 'blocked', message: `This job is canceled. Uncancel it before ${hold ? 'placing it on hold' : 'scheduling'}.`};
    case 'completed': return {kind: 'blocked', message: "This job is completed, so it can't be placed on hold."};
    default: return {kind: 'blocked', message: `This job has been invoiced, so it can't be ${hold ? 'placed on hold' : 'scheduled'}.`};
  }
};

/** What a tap on [action] does to a job at [status], with a saved schedule or not. */
export const jobActionOutcome = (action: JobAction, status: string | null | undefined, hasSchedule: boolean): JobActionOutcome | null => {
  if (!status) return null;
  const cancelled = isCancelledJob(status);
  const invoiced = INVOICED.includes(status);
  const completed = status === WorkOrderStatus.Completed;
  switch (action) {
    case 'schedule':
      if (cancelled) return blocked(action, 'cancelled');
      if (invoiced) return blocked(action, 'invoiced');
      if (completed) return {kind: 'confirm-reopen'};
      return {kind: 'open-schedule'};
    case 'hold':
      if (cancelled) return blocked(action, 'cancelled');
      if (invoiced) return blocked(action, 'invoiced');
      if (completed) return blocked(action, 'completed');
      // Taking a job off hold destroys nothing: no prompt.
      if (isOnHoldJob(status)) return {kind: 'apply', status: WorkOrderStatus.Confirmed};
      return hasSchedule
        ? {kind: 'confirm', action, status: WorkOrderStatus.OnHold, message: 'Placing this job on hold will cancel the schedule. Continue?'}
        : {kind: 'apply', status: WorkOrderStatus.OnHold};
    case 'cancel':
      // Uncancel: back to Confirmed, never Scheduled: canceling cleared the schedule.
      if (cancelled) return {kind: 'apply', status: WorkOrderStatus.Confirmed};
      if (invoiced || completed) {
        return {kind: 'confirm', action, status: WorkOrderStatus.Cancelled, message: 'This job has already been completed. Are you sure you want to cancel it?'};
      }
      return hasSchedule
        ? {kind: 'confirm', action, status: WorkOrderStatus.Cancelled, message: 'Canceling this job will delete the schedule. Continue?'}
        : {kind: 'apply', status: WorkOrderStatus.Cancelled};
  }
};

// ---- The schedule (JobSchedule.kt) ---------------------------------------------------------------

const parseDate = (value?: string | null) => {
  const m = value?.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : undefined;
};

const parseTime = (value?: string | null): [number, number] | undefined => {
  const m = value?.match(/^(\d{1,2}):(\d{2})/);
  return m ? [Number(m[1]), Number(m[2])] : undefined;
};

const clock = (h: number, m: number) => `${h % 12 || 12}:${String(m).padStart(2, '0')} ${h < 12 ? 'AM' : 'PM'}`;

/** "Wed, Oct 1" */
export const formatScheduledDate = (value?: string | null) =>
  parseDate(value)?.toLocaleDateString('en-US', {weekday: 'short', month: 'short', day: 'numeric'});

/** "8:30 AM" */
export const formatScheduledTime = (value?: string | null) => {
  const t = parseTime(value);
  return t ? clock(t[0], t[1]) : undefined;
};

/** The estimated finish: the start plus the crew's estimated on-site time. Never stored. */
export const estimatedFinishTime = (start?: string | null, onSiteMinutes?: number) => {
  const t = parseTime(start);
  if (!t || !onSiteMinutes || onSiteMinutes <= 0) return undefined;
  const finish = t[0] * 60 + t[1] + onSiteMinutes;
  return clock(Math.floor(finish / 60) % 24, finish % 60);
};

export const toIsoDate = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

// ---- Crew clocks (JobTimerView.kt) ------------------------------------------------------------

/** The nearest minute, half up (MeasuredTime). */
export const secondsToMinutes = (seconds: number) => Math.round(Math.max(0, seconds) / 60);

/** A running clock reads "01:04:32", fixed width. */
export const formatRunningClock = (totalSeconds: number) => {
  const s = Math.max(0, Math.floor(totalSeconds));
  return [Math.floor(s / 3600), Math.floor(s % 3600 / 60), s % 60].map(n => String(n).padStart(2, '0')).join(':');
};

/** The pencil moves whole minutes from the minute on screen and lands on a whole minute, never below zero. */
export const steppedCrewSeconds = (currentSeconds: number, deltaMinutes: number) =>
  Math.max(0, secondsToMinutes(currentSeconds) + deltaMinutes) * 60;

/** JOB TIME: the crew's combined time divided by the crew, to the nearest minute. */
export const jobWallMinutes = (seconds: number[]) =>
  seconds.length === 0 ? 0 : Math.round(seconds.reduce((a, b) => a + Math.max(0, b), 0) / 60 / seconds.length);

/** The pool the allocation splits: the crew's seconds added up and rounded once. */
export const pooledCrewMinutes = (seconds: number[]) => secondsToMinutes(seconds.reduce((a, b) => a + Math.max(0, b), 0));

// ---- The finish check (JobReview.kt finishJobChecks) -------------------------------------------

export interface FinishJobCheck {
  blockers: string[];
  warnings: string[];
}

const joinNames = (names: string[]) =>
  names.length <= 1 ? names.join('')
    : names.length === 2 ? `${names[0]} and ${names[1]}`
      : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;

/**
 * Crew time is all or none (SQ-559, Android's crewTimeBlocker): no crew, nobody timed or everyone timed passes;
 * only a crew where some have time and others do not blocks, naming the ones without. A work item or a machine
 * with no time only warns.
 */
export const finishJobChecks = (
  crew: {name: string; seconds: number}[], trees: EstimateTreeDto[], rate: number | null | undefined, label: (t: EstimateTreeDto) => string,
): FinishJobCheck => {
  const blockers: string[] = [];
  const idle = crew.filter(m => m.seconds <= 0).map(m => m.name);
  if (idle.length > 0 && idle.length < crew.length) {
    const one = idle.length === 1;
    blockers.push(`${joinNames(idle)} ${one ? 'has' : 'have'} no time on this job. Either unselect them in the crew selection at the top, `
      + `or add their time with the pencil next to ${one ? 'their name' : 'their names'}.`);
  }
  const warnings: string[] = [];
  trees.forEach(tree => {
    const minutes = tree.operations.reduce((s, op) => s + laborMinutes(op, rate), 0);
    if (minutes <= 0) warnings.push(`${label(tree)} has no time on it.`);
    (tree.equipment ?? []).filter(r => r.durationMinutes <= 0)
      .forEach(r => warnings.push(`${equipmentName(r)} on ${label(tree)} has no time recorded.`));
  });
  return {blockers, warnings};
};

// ---- Time allocation (JobTimeAllocation.kt, JobReview.kt) ----------------------------------------

/** Hamilton's split: floors, then the largest remainders, the earlier first on a tie; all-zero weights split evenly. */
export const proportionMinutes = (weights: number[], total: number): number[] => {
  if (weights.length === 0) return [];
  const safe = Math.max(0, total);
  if (safe === 0) return weights.map(() => 0);
  const sum = weights.reduce((a, b) => a + b, 0);
  if (sum <= 0) {
    const share = Math.floor(safe / weights.length);
    const rest = safe - share * weights.length;
    return weights.map((_, i) => share + (i < rest ? 1 : 0));
  }
  const exact = weights.map(w => safe * w / sum);
  const result = exact.map(v => Math.floor(v));
  let leftover = safe - result.reduce((a, b) => a + b, 0);
  const order = exact.map((v, i) => [i, v - Math.floor(v)] as const)
    .sort((a, b) => b[1] - a[1] || a[0] - b[0]).map(([i]) => i);
  for (let c = 0; leftover > 0 && order.length > 0; c++, leftover--) result[order[c % order.length]]++;
  return result;
};

/** Adds (negative: removes) [change] across [values] by [weights], never taking anyone below zero. */
export const absorb = (values: number[], weights: number[], change: number): number[] => {
  const result = [...values];
  if (result.length === 0 || change === 0) return result;
  let remaining = Math.abs(change);
  const adding = change > 0;
  while (remaining > 0) {
    const eligible = result.map((_, i) => i).filter(i => adding || result[i] > 0);
    if (eligible.length === 0) break;
    const shares = proportionMinutes(eligible.map(i => Math.max(0, weights[i] ?? 0)), remaining);
    let applied = 0;
    eligible.forEach((i, k) => {
      const share = adding ? shares[k] : Math.min(shares[k], result[i]);
      result[i] += adding ? share : -share;
      applied += share;
    });
    if (applied === 0) break;
    remaining -= applied;
  }
  return result;
};

/** Labor minutes (crew-minutes) per operation id. */
export type Allocation = Map<number, number>;

const estimatedOf = (trees: EstimateTreeDto[], rate?: number | null): Allocation =>
  new Map(trees.flatMap(t => t.operations.map(op => [op.id, laborMinutes(op, rate)] as const)));

const sumOf = (tree: EstimateTreeDto, allocation: Allocation) => tree.operations.reduce((s, op) => s + (allocation.get(op.id) ?? 0), 0);

/** The even split: the pool by estimated share, work items first, then their operations. */
export const proportionalAllocation = (trees: EstimateTreeDto[], pool: number, rate?: number | null): Allocation => {
  const est = estimatedOf(trees, rate);
  const items = proportionMinutes(trees.map(t => sumOf(t, est)), pool);
  const result: Allocation = new Map();
  trees.forEach((tree, i) => {
    const ops = proportionMinutes(tree.operations.map(op => est.get(op.id) ?? 0), items[i]);
    tree.operations.forEach((op, j) => result.set(op.id, ops[j]));
  });
  return result;
};

/** An operation's stored actual labor minutes (the server keeps seconds). */
export const storedActualMinutes = (op: EstimateTreeDto['operations'][number]) =>
  op.actualSeconds != null ? secondsToMinutes(op.actualSeconds) : op.actualMinutes ?? undefined;

/** The allocation the server holds, while it still adds up to the crew's time; else none. */
export const storedAllocation = (trees: EstimateTreeDto[], pool: number): Allocation | undefined => {
  const ops = trees.flatMap(t => t.operations);
  if (ops.length === 0 || ops.every(op => storedActualMinutes(op) === undefined)) return undefined;
  const stored: Allocation = new Map(ops.map(op => [op.id, storedActualMinutes(op) ?? 0]));
  return Array.from(stored.values()).reduce((a, b) => a + b, 0) === pool ? stored : undefined;
};

/** What the cards show: hand-moved figures while they still fit, else the stored split (when asked), else the even one. */
export const currentAllocation = (
  trees: EstimateTreeDto[], pool: number, rate: number | null | undefined, edits: Allocation | null, useStored: boolean,
): Allocation => {
  const ids = trees.flatMap(t => t.operations.map(op => op.id));
  if (edits && ids.every(id => edits.has(id)) && ids.reduce((s, id) => s + (edits.get(id) ?? 0), 0) === pool) {
    return new Map(ids.map(id => [id, edits.get(id) ?? 0]));
  }
  if (useStored) {
    const stored = storedAllocation(trees, pool);
    if (stored) return stored;
  }
  return proportionalAllocation(trees, pool, rate);
};

/** Sets work item [treeId]'s share to [newLabor]; the other work items give or take the difference by their estimates. */
export const moveWorkItemTime = (
  trees: EstimateTreeDto[], allocation: Allocation, rate: number | null | undefined, treeId: number, newLabor: number,
): Allocation => {
  const others = trees.filter(t => t.id !== treeId && t.operations.length > 0);
  const target = trees.find(t => t.id === treeId);
  if (!target || others.length === 0 || target.operations.length === 0) return allocation;
  const est = estimatedOf(trees, rate);
  const pool = trees.reduce((s, t) => s + sumOf(t, allocation), 0);
  const current = sumOf(target, allocation);
  const wanted = Math.min(Math.max(0, newLabor), pool);
  const before = others.map(t => sumOf(t, allocation));
  const after = absorb(before, others.map(t => sumOf(t, est)), current - wanted);
  const taken = before.reduce((a, b) => a + b, 0) - after.reduce((a, b) => a + b, 0);
  const result: Allocation = new Map(allocation);
  const respread = (tree: EstimateTreeDto, newTotal: number) => {
    const values = tree.operations.map(op => result.get(op.id) ?? 0);
    const updated = absorb(values, tree.operations.map(op => est.get(op.id) ?? 0), newTotal - values.reduce((a, b) => a + b, 0));
    tree.operations.forEach((op, i) => result.set(op.id, updated[i]));
  };
  respread(target, current + taken);
  others.forEach((tree, i) => respread(tree, after[i]));
  return result;
};

// ---- Equipment: billed against actual -------------------------------------------------------------

/** The time a machine actually ran: its review figure, else what it is billed for. */
export const actualRunMinutes = (row: EstimateTreeEquipmentDto) => row.actualDurationMinutes ?? row.durationMinutes;

const billedCost = (row: EstimateTreeEquipmentDto) => lockedCost(equipmentRow(row));
const actualCost = (row: EstimateTreeEquipmentDto) => lockedCost({...equipmentRow(row), durationMinutes: actualRunMinutes(row)});

/** Over (+) or under (−) what the row is billed, at the same locked rate. */
export const rowOverUnder = (row: EstimateTreeEquipmentDto) => actualCost(row) - billedCost(row);

export const equipmentOverUnder = (rows: EstimateTreeEquipmentDto[]) => rows.reduce((s, r) => s + rowOverUnder(r), 0);

/**
 * The job's equipment over/under: a machine with a dollar minimum is judged
 * once for the whole job (what it should have been charged against what it
 * was), so time that stays under the minimum costs or saves nothing.
 * [coveredByMinimum]: the work items ran over and the minimums absorbed it all.
 */
export const jobEquipmentOverUnder = (rows: EstimateTreeEquipmentDto[]): {dollars: number; coveredByMinimum: boolean} => {
  let raw = 0;
  let adjusted = 0;
  const byMachine = new Map<number, EstimateTreeEquipmentDto[]>();
  rows.forEach(r => byMachine.set(r.equipmentId, [...(byMachine.get(r.equipmentId) ?? []), r]));
  byMachine.forEach(machineRows => {
    const time = equipmentOverUnder(machineRows);
    raw += time;
    const billedRows = machineRows.filter(r => r.durationMinutes > 0);
    const minimums = billedRows.map(r => r.minimumCharge).filter((m): m is number => m != null);
    if (minimums.length === 0) {
      adjusted += time;
    } else {
      const minimum = Math.max(...minimums);
      const billed = billedRows.reduce((s, r) => s + billedCost(r), 0);
      const actual = machineRows.reduce((s, r) => s + actualCost(r), 0);
      adjusted += Math.max(actual, minimum) - Math.max(billed, minimum);
    }
  });
  return {dollars: adjusted, coveredByMinimum: Math.round(raw * 100) > 0 && Math.round(adjusted * 100) === 0};
};

/** "+$75.00 over" / "−$20.00 under"; nothing when it ran what it is billed. */
export const overUnderTag = (dollars: number) => {
  const cents = Math.round(dollars * 100);
  if (cents === 0) return undefined;
  return cents > 0 ? `+${money(dollars)} over` : `−${money(-dollars)} under`;
};

/**
 * The line under a machine in review: "Ran 3h · $75.00 over the $375.00 billed"; [1] true when over.
 * Not [judged] (the actuals were hidden, seesActuals): only "Ran 3h", and only when it differs from billed.
 */
export const machineRanLine = (row: EstimateTreeEquipmentDto, judged = true): [string, boolean] | undefined => {
  if (row.actualDurationMinutes == null) return undefined;
  if (!judged) return actualRunMinutes(row) !== row.durationMinutes ? [`Ran ${formatMinutes(actualRunMinutes(row))}`, false] : undefined;
  const billed = billedCost(row);
  const delta = rowOverUnder(row);
  const cents = Math.round(delta * 100);
  const ran = `Ran ${formatMinutes(actualRunMinutes(row))}`;
  if (cents > 0) return [`${ran} · ${money(delta)} over the ${money(billed)} billed`, true];
  if (cents < 0) return [`${ran} · ${money(-delta)} under the ${money(billed)} billed`, false];
  return actualRunMinutes(row) !== row.durationMinutes ? [`${ran} · same as billed`, false] : undefined;
};

// ---- Was It Bid Right? ----------------------------------------------------------------------------

export type VerdictKind = 'underbid' | 'overbid' | 'right';

export interface BidVerdict {
  kind: VerdictKind;
  dollars: number;
  /** The part of it that is equipment running over (+) or under (−); undefined at an operation. */
  equipment?: number;
}

/** The labor difference priced at the crew's rate, plus equipment's over/under, compared in cents. */
export const bidVerdict = (actualLabor: number, estimatedLabor: number, rate?: number | null, equipment?: number): BidVerdict | undefined => {
  const eq = equipment ?? 0;
  if (estimatedLabor <= 0 && eq === 0) return undefined;
  const total = (actualLabor - estimatedLabor) / 60 * Math.max(0, rate ?? 0) + eq;
  const cents = Math.round(total * 100);
  if (cents === 0) {
    const kind: VerdictKind = actualLabor === estimatedLabor ? 'right' : actualLabor > estimatedLabor ? 'underbid' : 'overbid';
    return {kind, dollars: 0, equipment};
  }
  return cents > 0 ? {kind: 'underbid', dollars: total, equipment} : {kind: 'overbid', dollars: -total, equipment};
};

export const verdictText = (v: BidVerdict) =>
  v.kind === 'right' ? 'Just Right' : `${v.kind === 'underbid' ? 'Underbid' : 'Overbid'} by ${money(v.dollars)}`;

/** "Includes equipment running over by $X" / "… coming in under by $X"; nothing when it ran as billed. */
export const equipmentContribution = (dollars?: number) => {
  if (dollars == null) return undefined;
  const cents = Math.round(dollars * 100);
  if (cents > 0) return `Includes equipment running over by ${money(dollars)}`;
  if (cents < 0) return `Includes equipment coming in under by ${money(-dollars)}`;
  return undefined;
};

/** One scope's allocated time (wall-clock, crew-normalized) and its verdict. */
export interface AllocatedScope {
  wallMinutes: number;
  crew: number;
  verdict?: BidVerdict;
  /** SQ-559: a finished job with no crew time; the card says so in place of the time and the verdict. */
  noCrewTime?: boolean;
}

const wallClock = (labor: number, crew: number) => crew <= 1 ? labor : Math.round(labor / crew);

const scope = (allocated: number, estimated: number, crew: number, rate?: number | null, equipment?: number): AllocatedScope => {
  const count = Math.max(1, crew);
  return {wallMinutes: wallClock(allocated, count), crew: count, verdict: bidVerdict(allocated, estimated, rate, equipment)};
};

export interface JobAllocationResult {
  job: AllocatedScope;
  byWorkItem: Map<number, AllocatedScope>;
  byOperation: Map<number, AllocatedScope>;
}

/** The completed job's readouts: the pool split over the work (or as stored), each scope against its estimate. */
export const allocateJobTime = (
  trees: EstimateTreeDto[], pool: number, crew: number, rate: number | null | undefined, allocation: Allocation,
): JobAllocationResult => {
  // SQ-559 (Android's noCrewTimeAllocation): no crew time, so nothing is split or judged.
  if (pool <= 0) {
    const none: AllocatedScope = {wallMinutes: 0, crew: Math.max(1, crew), noCrewTime: true};
    return {
      job: none,
      byWorkItem: new Map(trees.map(t => [t.id, none])),
      byOperation: new Map(trees.flatMap(t => t.operations).map(op => [op.id, none])),
    };
  }
  const est = estimatedOf(trees, rate);
  const byWorkItem = new Map<number, AllocatedScope>();
  const byOperation = new Map<number, AllocatedScope>();
  trees.forEach(tree => {
    tree.operations.forEach(op => byOperation.set(op.id, scope(allocation.get(op.id) ?? 0, est.get(op.id) ?? 0, crew, rate)));
    const rows = tree.equipment ?? [];
    byWorkItem.set(tree.id, scope(sumOf(tree, allocation), sumOf(tree, est), crew, rate, rows.length > 0 ? equipmentOverUnder(rows) : undefined));
  });
  const all = trees.flatMap(t => t.equipment ?? []);
  const estimatedTotal = Array.from(est.values()).reduce((a, b) => a + b, 0);
  return {
    job: scope(Math.max(0, pool), estimatedTotal, crew, rate, all.length > 0 ? jobEquipmentOverUnder(all).dollars : undefined),
    byWorkItem,
    byOperation,
  };
};

/** Two allocations put the same minutes on the same operations. */
export const sameAllocation = (a: Allocation, b: Allocation) =>
  a.size === b.size && Array.from(a.entries()).every(([id, minutes]) => b.get(id) === minutes);
